import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, peso, fecha, hoy } from '../utils/formato';
import { porFinca, totales, sinCedula, aCSV, nombreDelArchivo } from '../utils/reporte';

// El reporte que pide el contador para la DIAN.
//
// Lo llevaban en un Excel a mano: una hoja por finca, una fila por día, con las
// columnas de vaca y búfala aparte. Esto es esa misma hoja, sacada de lo que ya
// está anotado — y con la cédula, que es lo que la hace presentable.

const primeroDelMes = () => hoy().slice(0, 8) + '01';

function Contador() {
  const [desde, setDesde] = useState(primeroDelMes);
  const [hasta, setHasta] = useState(hoy);
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState('');
  const [abierta, setAbierta] = useState(null);

  const cargar = useCallback(() => {
    setFilas(null);
    axios.get('/api/reportes/compras-leche', { params: { desde, hasta } })
      .then(r => { setFilas(r.data || []); setError(''); })
      .catch(() => setError('No se pudo armar el reporte. Recargue; si sigue igual, avise.'));
  }, [desde, hasta]);

  useEffect(cargar, [cargar]);

  const bajar = () => {
    // El BOM al principio: sin él, Excel abre el archivo en su codificación de
    // siempre y «Peña» sale como «PeÃ±a» en toda la columna de nombres.
    const csv = '﻿' + aCSV(fincas, { desde, hasta });
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = nombreDelArchivo(desde, hasta);
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) return <div className="aviso malo">{error}</div>;

  const fincas = filas ? porFinca(filas) : [];
  const t = totales(fincas);
  const faltan = filas ? sinCedula(filas) : [];

  return (
    <>
      <div className="encabezado">
        <h1>Reporte para el contador</h1>
        <p>Las compras de leche del periodo, con cédula de cada proveedor</p>
      </div>

      <div className="bloque">
        <div className="cuerpo">
          <div className="campos">
            <label className="campo">
              <span>Desde</span>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
            </label>
            <label className="campo">
              <span>Hasta</span>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
            </label>
          </div>
          <div className="acciones">
            <button className="boton suave" onClick={() => window.print()}>
              🖨️ Imprimir
            </button>
            <button className="boton" onClick={bajar} disabled={!filas || filas.length === 0}>
              ⬇️ Bajar para Excel
            </button>
          </div>
        </div>
      </div>

      {/* LO QUE BLOQUEA LA PRESENTACIÓN, arriba de todo.
          Un reporte perfecto con tres proveedores sin identificar no se puede
          presentar. Más vale verlo antes de mandárselo al contador que después
          de que él lo devuelva. */}
      {faltan.length > 0 && (
        <div className="aviso malo">
          <strong>
            {faltan.length === 1
              ? 'Falta la cédula de 1 proveedor'
              : `Faltan las cédulas de ${faltan.length} proveedores`}
          </strong>
          {' '}— sin cédula esos renglones no sirven para declarar: la DIAN no
          identifica a nadie por el nombre de la finca. Se ponen en{' '}
          <strong>🚜 Fincas → ✏️</strong>.
          <div style={{ marginTop: 8 }}>
            {faltan.map(f => (
              <div key={f.finca_id} style={{ fontSize: 13 }}>
                · {f.finca}{f.propietario ? ` (${f.propietario})` : ''} — {plata(f.total)}
              </div>
            ))}
          </div>
        </div>
      )}

      {!filas ? (
        <p style={{ color: 'var(--tinta-tenue)' }}>Armando el reporte…</p>
      ) : filas.length === 0 ? (
        <div className="bloque">
          <div className="vacio">No hay compras de leche anotadas en ese periodo.</div>
        </div>
      ) : (
        <>
          <div className="rejilla">
            <div className="dato" style={{ '--color': 'var(--leche)' }}>
              <div className="rotulo">🥛 Comprado</div>
              <div className="valor cifra">{plata(t.total)}</div>
              <div className="pie">
                {peso(t.litros, 'L')} a {t.proveedores}{' '}
                {t.proveedores === 1 ? 'proveedor' : 'proveedores'}
              </div>
            </div>
            <div className="dato" style={{ '--color': 'var(--acento-vivo)' }}>
              <div className="rotulo">🐄 Vaca</div>
              <div className="valor cifra">{plata(t.valorVaca)}</div>
              <div className="pie">{peso(t.litrosVaca, 'L')}</div>
            </div>
            {t.litrosBufala > 0 && (
              <div className="dato" style={{ '--color': 'var(--ojo)' }}>
                <div className="rotulo">🐃 Búfala</div>
                <div className="valor cifra">{plata(t.valorBufala)}</div>
                <div className="pie">{peso(t.litrosBufala, 'L')}</div>
              </div>
            )}
          </div>

          <div className="bloque">
            <h2>
              🧾 Por proveedor
              <span className="contador">{fincas.length}</span>
            </h2>
            <div className="tabla-caja">
              <table className="datos">
                <thead>
                  <tr>
                    <th>Cédula / NIT</th>
                    <th>Proveedor</th>
                    <th style={{ textAlign: 'right' }}>Litros vaca</th>
                    <th style={{ textAlign: 'right' }}>Valor vaca</th>
                    <th style={{ textAlign: 'right' }}>Litros búfala</th>
                    <th style={{ textAlign: 'right' }}>Valor búfala</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {fincas.map(f => (
                    <React.Fragment key={f.finca_id}>
                      <tr>
                        <td className="recio" style={{
                          color: f.documento ? undefined : 'var(--malo)' }}>
                          {f.documento || 'FALTA'}
                        </td>
                        <td className="recio">
                          {f.propietario || f.finca}
                          <div style={{ fontSize: 12, fontWeight: 400,
                                        color: 'var(--tinta-tenue)' }}>
                            {f.finca}{f.ubicacion ? ` · ${f.ubicacion}` : ''}
                          </div>
                        </td>
                        <td className="num">{f.litrosVaca > 0 ? peso(f.litrosVaca, 'L') : '—'}</td>
                        <td className="num">{f.valorVaca > 0 ? plata(f.valorVaca) : '—'}</td>
                        <td className="num">{f.litrosBufala > 0 ? peso(f.litrosBufala, 'L') : '—'}</td>
                        <td className="num">{f.valorBufala > 0 ? plata(f.valorBufala) : '—'}</td>
                        <td className="num recio">{plata(f.total)}</td>
                        <td>
                          <button className="boton chico suave"
                                  onClick={() => setAbierta(abierta === f.finca_id ? null : f.finca_id)}>
                            {abierta === f.finca_id ? 'Ocultar' : `${f.dias.length} días`}
                          </button>
                        </td>
                      </tr>
                      {/* El día por día, que es la hoja del Excel. Va plegado:
                          con veinte fincas a treinta días son seiscientos
                          renglones, y quien busca un total no los quiere ver. */}
                      {abierta === f.finca_id && f.dias.map((d, n) => (
                        <tr key={n} style={{ background: 'var(--hondo)' }}>
                          <td></td>
                          <td style={{ fontSize: 13, color: 'var(--tinta-tenue)' }}>
                            {fecha(d.fecha)}
                          </td>
                          <td className="num" style={{ fontSize: 13 }}>
                            {d.litrosVaca > 0 ? peso(d.litrosVaca, 'L') : ''}
                          </td>
                          <td className="num" style={{ fontSize: 13 }}>
                            {d.valorVaca > 0 ? plata(d.valorVaca) : ''}
                          </td>
                          <td className="num" style={{ fontSize: 13 }}>
                            {d.litrosBufala > 0 ? peso(d.litrosBufala, 'L') : ''}
                          </td>
                          <td className="num" style={{ fontSize: 13 }}>
                            {d.valorBufala > 0 ? plata(d.valorBufala) : ''}
                          </td>
                          <td className="num" style={{ fontSize: 13 }}>
                            {plata(d.valorVaca + d.valorBufala)}
                          </td>
                          <td></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr style={{ borderTop: '2px solid var(--tinta)' }}>
                    <td></td>
                    <td className="recio">TOTAL</td>
                    <td className="num recio">{peso(t.litrosVaca, 'L')}</td>
                    <td className="num recio">{plata(t.valorVaca)}</td>
                    <td className="num recio">{peso(t.litrosBufala, 'L')}</td>
                    <td className="num recio">{plata(t.valorBufala)}</td>
                    <td className="num recio">{plata(t.total)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Contador;
